import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import api from "../services/api";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";

const initialForm = {
  name: "",
  cost_center: "",
  is_active: true,
};

const CollaboratorDepartments = () => {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await api.get("/collaborators/departments", {
        params: { include_inactive: true },
      });
      setDepartments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao carregar departamentos:", error);
      alert("Erro ao carregar departamentos.");
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("Informe o nome do departamento.");
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await api.put(`/collaborators/departments/${editingId}`, form);
      } else {
        await api.post("/collaborators/departments", form);
      }
      resetForm();
      await loadDepartments();
    } catch (error) {
      console.error("Erro ao salvar departamento:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao salvar departamento.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (department) => {
    setEditingId(department.id);
    setForm({
      name: department.name || "",
      cost_center: department.cost_center || "",
      is_active: department.is_active !== false,
    });
  };

  const handleDelete = async (departmentId) => {
    if (!window.confirm("Tem certeza que deseja excluir este departamento? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/collaborators/departments/${departmentId}`);
      if (editingId === departmentId) resetForm();
      await loadDepartments();
    } catch (error) {
      console.error("Erro ao excluir departamento:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao excluir departamento.");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" aria-label="Voltar" onClick={() => navigate("/collaborators")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Departamentos</h1>
            <p className="text-gray-500">Cadastre e mantenha a lista de departamentos dos colaboradores.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingId ? "Editar departamento" : "Novo departamento"}
                </h2>
                <p className="text-sm text-gray-500">Esse cadastro abastece a lista suspensa dos colaboradores.</p>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  placeholder="Nome do departamento"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  placeholder="Centro de custo padrão"
                  value={form.cost_center}
                  onChange={(e) => setForm((prev) => ({ ...prev, cost_center: e.target.value }))}
                />
                <select
                  value={form.is_active ? "true" : "false"}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.value === "true" }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
                <div className="flex gap-2">
                  <Button type="submit" disabled={saving}>
                    {saving ? "Salvando..." : editingId ? "Salvar alteração" : "Cadastrar"}
                  </Button>
                  {editingId && (
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancelar edição
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Lista de departamentos</h2>
                <p className="text-sm text-gray-500">Use departamentos ativos para seleção nos colaboradores.</p>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-8 text-center text-gray-500">Carregando departamentos...</div>
              ) : departments.length === 0 ? (
                <div className="py-8 text-center text-gray-500">Nenhum departamento cadastrado.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px]">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Nome</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Centro de custo</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.map((department) => (
                        <tr key={department.id} className="border-b border-gray-100">
                          <td className="px-4 py-3 text-sm text-gray-900">{department.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{department.cost_center || "—"}</td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                department.is_active !== false
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {department.is_active !== false ? "Ativo" : "Inativo"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                onClick={() => handleEdit(department)}
                                className="h-auto px-3 py-1 text-xs text-gray-700"
                              >
                                Editar
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => handleDelete(department.id)}
                                className="h-auto px-3 py-1 text-xs border-red-200 text-red-700 hover:bg-red-50"
                              >
                                Excluir
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default CollaboratorDepartments;
